import { HttpClient } from "@angular/common/http";
import { Injectable, computed, effect, inject, signal } from "@angular/core";
import { TranslocoService, Translation } from "@jsverse/transloco";
import { TranslocoLocaleService } from "@jsverse/transloco-locale";
import { catchError, firstValueFrom, of } from "rxjs";

import { environment } from "../../../environments/environment";
import { LocaleService } from "../services/locale.service";
import { SpineAuthorizationService } from "../surface/spine-authorization.service";

import { i18nAssetUrl } from "./i18n-asset-url";
import { LanguageCoverageService } from "./language-coverage.service";
import { TenantLanguagesService } from "./tenant-languages.service";

import {
  APP_LANGUAGES,
  AppLanguage,
  DEFAULT_LANGUAGE,
  IndustryPreset,
  LANGUAGE_REGIONS,
  LANGUAGE_STORAGE_KEY,
  LanguageRegion,
  resolveIndustryPreset
} from "./i18n.tokens";

/** One heading in the language menu, with the languages filed under it. */
export interface LanguageGroup {
  readonly region: LanguageRegion;
  readonly languages: readonly AppLanguage[];
}

// Runtime language switching (no reload) plus the industry-terminology overlay.
@Injectable({ providedIn: "root" })
export class LanguageService {
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(TranslocoLocaleService);
  private readonly orgLocale = inject(LocaleService);
  private readonly http = inject(HttpClient);

  private readonly activeCode = signal<string>(DEFAULT_LANGUAGE);
  private readonly activePreset = signal<IndustryPreset>("standard");

  private readonly tenantLanguages = inject(TenantLanguagesService);
  private readonly spine = inject(SpineAuthorizationService);
  private readonly coverage = inject(LanguageCoverageService);

  /**
   * The languages this reader may switch to.
   *
   * The portal ships a fixed set; a tenant narrows it to the ones it actually offers. Filtered
   * here rather than in the switcher so every entry point agrees — a language absent from the
   * menu but reachable some other way is a setting that does not hold.
   *
   * A tenant that has chosen nothing gets all of them, and so does a session whose settings read
   * has not landed: an empty menu is a worse answer than a wide one.
   */
  readonly languages = computed<readonly AppLanguage[]>(() => {
    const offered = new Set(this.tenantLanguages.current().enabled);
    const allowed = APP_LANGUAGES.filter((l) => offered.has(l.code));
    return this.withoutUntranslated(allowed.length > 0 ? allowed : APP_LANGUAGES);
  });

  /** @see visibleLanguages */
  private withoutUntranslated(list: readonly AppLanguage[]): readonly AppLanguage[] {
    return visibleLanguages(list, this.coverage.loaded(), (code) => this.coverage.reading(code).untranslated);
  }

  /**
   * The same list, split by region, for a menu rather than a lookup.
   *
   * Thirty entries in one flat `<select>` is a scrolling problem, and the split people
   * actually navigate by is "my country's languages" versus "everything else". A region with
   * nothing offered is dropped rather than rendered empty, so a tenant offering only English
   * and Assamese sees one group, not two with a hole in one.
   */
  readonly languageGroups = computed<readonly LanguageGroup[]>(() => {
    const offered = this.languages();
    return LANGUAGE_REGIONS.map((region) => ({
      region,
      languages: offered.filter((l) => l.region === region)
    })).filter((group) => group.languages.length > 0);
  });

  readonly active = this.activeCode.asReadonly();
  readonly preset = this.activePreset.asReadonly();

  readonly activeLanguage = computed<AppLanguage>(
    () => APP_LANGUAGES.find((l) => l.code === this.activeCode()) ?? APP_LANGUAGES[0]
  );

  constructor() {
    // The organisation's time-format preference reaches the screen through the formatting
    // locale, not through 332 individual call sites. Re-applied as an effect because the
    // preference arrives with the session, after startup has already chosen a language.
    effect(() => {
      this.orgLocale.timeFormat();
      this.activeLanguage();
      this.applyFormattingLocale();
    });
  }

  /**
   * Resolves the startup language (stored choice, then browser, then default) and blocks
   * startup until its root translation is in memory.
   *
   * Components under a Transloco scope re-render when their own scope file loads, not when
   * the root file does — so if the root fetch loses that race, every root-scope key on the
   * page (`surface.*`, `common.*` buttons) freezes as its raw literal until the next
   * in-app navigation. Awaiting the root load here removes the race for the whole app; a
   * failed fetch still lets the app start, since raw keys beat a blank page.
   */
  async init(): Promise<void> {
    this.setLanguage(this.resolveInitialLanguage(), false);
    this.applyStoredPreset();
    try {
      await firstValueFrom(this.transloco.load(this.activeCode()));
    } catch {
      // Offline or blocked asset: start anyway, keys resolve on the next successful load.
    }
  }

  /**
   * Applies the tenant's industry terminology overlay from the stored session.
   *
   * The stored value is `account.category`, which is a display label for tenants created before
   * the onboarding wizard existed. Comparing it directly against the preset ids matched only the
   * wizard's own spelling, so "Health Wellness and Fitness" resolved to nothing and the tenant
   * kept generic vocabulary; `resolveIndustryPreset` reads both vocabularies.
   */
  applyStoredPreset(): void {
    const preset = resolveIndustryPreset(this.read("industry_type"));
    if (preset !== "standard") {
      this.applyPreset(preset);
    }
  }

  /**
   * Applies a language, and — when a person chose it — remembers it for them.
   *
   * `chosen` separates the two callers. Startup is resolving a language, not choosing one, and
   * writing it back would turn a browser default into a stated preference the reader never
   * made. A picker is choosing, so it persists: to this device immediately, and to the account
   * so the choice follows the reader to their phone.
   *
   * The server write is deliberately not awaited and its failure is deliberately ignored. The
   * language has already changed on screen by then; making that wait on a round trip, or undoing
   * it because the round trip failed, would be worse than a preference that has to be set twice.
   */
  setLanguage(code: string, chosen = true): void {
    // Checked against what the tenant offers, not merely against what the portal ships: a code
    // arriving from a stored preference or a stale tab must not reinstate a withdrawn language.
    const language = this.languages().find((l) => l.code === code);
    if (!language) {
      return;
    }

    this.transloco.setActiveLang(language.code);
    this.activeCode.set(language.code);
    this.applyFormattingLocale();

    const root = document.documentElement;
    root.lang = language.code;
    root.dir = language.dir;

    this.persist(language.code);
    if (chosen) {
      this.persistToAccount(language.code);
    }

    if (this.activePreset() !== "standard") {
      this.applyPreset(this.activePreset());
    }
  }

  /**
   * The locale every date, time, number and currency on screen is formatted through.
   *
   * The language decides the words; the organisation's setting decides whether a time reads
   * `14:30` or `2:30 PM`. Carrying the second as the `-u-hc-` extension on the formatting tag is
   * what makes one setting reach every surface: `Intl` honours it, so a screen that formats a
   * time correctly today cannot start ignoring the preference tomorrow by forgetting to ask.
   *
   * `setActiveLang` also drives a locale change of its own (Transloco Locale subscribes to it and
   * applies the bare mapping), so this must run after it — otherwise the extension is dropped a
   * moment after being set.
   */
  private applyFormattingLocale(): void {
    const cycle = this.orgLocale.timeFormat() === "24h" ? "h23" : "h12";
    this.locale.setLocale(`${this.activeLanguage().locale}-u-hc-${cycle}`);
  }

  /**
   * Stores the reader's choice on their account, so it is theirs rather than this browser's.
   *
   * `PATCH /me/preferences` is ungated by design — these are the caller's own settings and the
   * caller is resolved from the token, so a learner needs no grant to set their own language.
   * That is the whole point: choosing a language is not an administrative act, and only *which
   * languages are offered* is.
   */
  private persistToAccount(code: string): void {
    this.http
      .patch(`${environment.apiUrl}/me/preferences`, { locale: code })
      .pipe(catchError(() => of(null)))
      .subscribe(() => this.spine.patchAccount({ locale: code }));
  }

  // A missing overlay file is not an error: the preset simply ships no overrides.
  applyPreset(preset: IndustryPreset): void {
    this.activePreset.set(preset);
    if (preset === "standard") {
      return;
    }

    const lang = this.activeCode();
    this.http
      .get<Translation>(i18nAssetUrl(`assets/i18n/terms/${lang}.${preset}.json`))
      .pipe(catchError(() => of<Translation>({})))
      .subscribe((overrides) => {
        this.transloco.setTranslation(overrides, lang, { merge: true });
      });
  }

  /**
   * Startup language: the reader's stored choice, then their browser's, then the tenant's.
   *
   * Deliberately checked against `APP_LANGUAGES` rather than against the tenant's set. This runs
   * before the settings read can have landed, so consulting the tenant here would narrow to the
   * unrestricted default anyway — and would then need a second pass to correct itself, visibly,
   * after the reader had already started reading.
   */
  private resolveInitialLanguage(): string {
    const stored = this.read(LANGUAGE_STORAGE_KEY);
    if (stored !== null && APP_LANGUAGES.some((l) => l.code === stored)) {
      return stored;
    }

    // The account's own saved choice, when the authorization payload is already in hand. It
    // outranks the browser because it is a preference the person stated; the browser's is a
    // guess about them. It does not outrank this device's stored choice, which is newer.
    const saved = this.spine.account()?.locale;
    if (saved !== undefined && APP_LANGUAGES.some((l) => l.code === saved)) {
      return saved;
    }

    const browser = navigator.language.split("-")[0];
    if (APP_LANGUAGES.some((l) => l.code === browser)) {
      return browser;
    }

    return DEFAULT_LANGUAGE;
  }

  // localStorage throws in private-mode Safari and when storage is disabled;
  // language preference is never worth breaking startup over.
  private persist(code: string): void {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
    } catch {
      // ignore
    }
  }

  private read(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }
}

/**
 * Drops languages with no bundle at all.
 *
 * A language offered with nothing behind it is worse than one that is missing: the reader picks
 * their own language by its native name and gets an entirely English portal, which reads as the
 * product being broken rather than as the translation being unwritten. Manipuri, Bodo, Khasi and
 * Mizo are all in that state today.
 *
 * Driven by the coverage manifest rather than by a list of codes, so a language reappears the
 * moment someone translates it and nobody has to remember to unhide it. Partially translated
 * languages stay: chrome-only is a usable state, and the picker already labels it.
 *
 * `known` is what stops this emptying the menu. Coverage cannot tell "nothing is translated"
 * from "the manifest has not arrived", and those must not lead to the same answer.
 */
export function visibleLanguages(
  list: readonly AppLanguage[],
  known: boolean,
  untranslated: (code: string) => boolean
): readonly AppLanguage[] {
  if (!known) {
    return list;
  }
  const shown = list.filter((l) => l.code === DEFAULT_LANGUAGE || !untranslated(l.code));
  return shown.length > 0 ? shown : list;
}
